import * as React from 'react';
import { ErrorBoundaryLabel } from 'utility';
import { ErrorBoundary } from 'bernie-view';
import { SystemEventLevel } from 'bernie-logger';

import { SmartShoppingListingError, FlightsListings } from '@shared-ui/retail-flights-smart-shopping-listings';
import { openOffer } from 'utility';
import { areSameFiltersValuesList, hasSameNumberOfFilters, triggerSearchFromSideSheet, logEvent } from './utils';
import { useSmartShoppingListingsInput, useStores } from 'utility/hooks';

import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { AddOnRequested, FlightsSearchFilterValuesInput } from 'src/__generated__/api';
import { removePill, selectClearAllFilters } from '../PWA_USF_FSR';

type SmartListings = {
  isSmallViewPort?: boolean;
};

export const SmartListings = observer(function SmartListings(props: SmartListings) {
  const { isSmallViewPort } = props;
  const { listingsStore, searchCriteriaStore, errorStore } = useStores();
  const smartShoppingInput = useSmartShoppingListingsInput();
  const { context, dialogsStore, sortFilterStore } = useStores();
  const location = useLocation();
  const history = useHistory();

  const onLoad = React.useCallback(() => {
    logEvent(SystemEventLevel.INFO, 'SMART_SHOPPING_LOADED_LISTINGS', 'displaying new smart shoppping offers');
    listingsStore.setSmartListingsLoaded();
    sortFilterStore.setShowFiltersUSF(true);
  }, [listingsStore, sortFilterStore]);

  const onError = React.useCallback(
    (listingsErrorType: SmartShoppingListingError) => {
      listingsStore.setSmartListingsError(listingsErrorType);
      if (listingsErrorType === SmartShoppingListingError.NO_API_RESPONSE) {
        errorStore.noApiResponse = true;
      }
    },
    [listingsStore, errorStore]
  );

  const onTriggerClick = React.useCallback(
    response => {
      if (response.__typename === 'FlightsNavigateToDetails') {
        const relativePath = response.nextPage.__typename === 'HttpURI' ? response.nextPage.relativePath : null;
        const packageUrl = response.packagesUrl?.__typename === 'HttpURI' ? response.packagesUrl.relativePath : null;

        setTimeout(() => {
          const isForcedChoiceModalDisabled =
            !dialogsStore.isForcedChoiceEnabled || isSmallViewPort || packageUrl === null;
          if (isForcedChoiceModalDisabled) {
            openOffer(relativePath, isSmallViewPort);
          } else {
            dialogsStore.updateForcedChoiceModal(relativePath, packageUrl);
          }
        }, 50);
      } else {
        const { dialogSideSheet, searchCriteria } = searchCriteriaStore;
        setTimeout(() => {
          searchCriteriaStore.sideSheetQueryContext.journeyContinuationId = response;
          dialogSideSheet.searchFilterValuesList = JSON.parse(JSON.stringify(searchCriteria.searchFilterValuesList));
          dialogSideSheet.addOnRequested = JSON.parse(
            JSON.stringify(searchCriteriaStore.sideSheetQueryContext.addOnRequested)
          );
        }, 10);
      }
    },
    [dialogsStore, isSmallViewPort, searchCriteriaStore]
  );

  const onDialogClose = React.useCallback(
    (flightSearchFilterValuesList: FlightsSearchFilterValuesInput[], addOnRequested: AddOnRequested[]) => {
      const { searchFilterValuesList } = searchCriteriaStore.searchCriteria;
      const sameAddOns =
        hasSameNumberOfFilters(searchFilterValuesList, flightSearchFilterValuesList) &&
        areSameFiltersValuesList(searchFilterValuesList, flightSearchFilterValuesList);

      if (!sameAddOns) {
        searchCriteriaStore.searchCriteria.searchFilterValuesList = JSON.parse(
          JSON.stringify(flightSearchFilterValuesList)
        );
        searchCriteriaStore.sideSheetQueryContext.addOnRequested = JSON.parse(JSON.stringify(addOnRequested));
        triggerSearchFromSideSheet(searchCriteriaStore, context, history, location);
      }

      return;
    },
    [searchCriteriaStore, context, history, location]
  );

  const onRemoveAllFilters = React.useCallback((): void => {
    selectClearAllFilters(searchCriteriaStore, context, history, location);
  }, [context, history, location, searchCriteriaStore]);

  const onRemoveFilter = React.useCallback(
    filter => {
      removePill(filter, searchCriteriaStore, context, history, location);
    },
    [context, history, location, searchCriteriaStore]
  );
  const isLoading = React.useCallback(
    (loadingState: boolean) => {
      listingsStore.setSmartListingsLoading(loadingState);
    },
    [listingsStore]
  );

  const onSuccessSideSheet = React.useCallback(() => {
    logEvent(SystemEventLevel.INFO, 'SMART_SHOPPING_SUCCESS_FSR_SIDE_SHEET', 'displaying data in FSR side sheet');
  }, []);

  const onErrorSideSheet = React.useCallback(() => {
    logEvent(SystemEventLevel.ERROR, 'SMART_SHOPPING_ERROR_FSR_SIDE_SHEET', 'displaying error in FSR side sheet');
  }, []);

  if (!searchCriteriaStore.searchCriteria.journeyCriteria[0].departureDate.day) {
    return null;
  }

  return (
    <ErrorBoundary label={ErrorBoundaryLabel.SMART_SHOPPING_LISTINGS}>
      <FlightsListings
        inputs={smartShoppingInput}
        onDialogClose={onDialogClose}
        onError={onError}
        onErrorSideSheet={onErrorSideSheet}
        onFareApplied={onTriggerClick}
        onLoad={onLoad}
        onLoading={isLoading}
        onOverFilteringRemoveAllClick={onRemoveAllFilters}
        onOverFilteringRemovePillClick={onRemoveFilter}
        onSuccessSideSheet={onSuccessSideSheet}
      />
    </ErrorBoundary>
  );
});
